import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trash2, Edit3, Plus, X, LogOut, Calendar, BookOpen, Mail, FileText, Settings, HelpCircle } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import ContentEditor from "@/components/ContentEditor";
import SmtpSettings from "@/components/SmtpSettings";
import TelegramSettings from "@/components/TelegramSettings";
import ChangePassword from "@/components/ChangePassword";
import Manual from "@/components/Manual";

const TAB = { BOOKINGS: "bookings", BLOG: "blog", MESSAGES: "messages", CONTENT: "content", SETTINGS: "settings", MANUAL: "manual" };

const STATUSES = ["nowa", "potwierdzona", "odbyta", "anulowana"];

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(TAB.BOOKINGS);

  const [bookings, setBookings] = useState([]);
  const [posts, setPosts] = useState([]);
  const [messages, setMessages] = useState([]);

  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editingPost, setEditingPost] = useState(null); // null = none, {} = new, {...post} = edit
  const [savingPost, setSavingPost] = useState(false);
  const [smtp, setSmtp] = useState(null);
  const [telegram, setTelegram] = useState(null);

  const loadAll = async () => {
    try {
      const [b, p, m, s, t] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/blog"),
        api.get("/admin/contact"),
        api.get("/admin/settings/smtp"),
        api.get("/admin/settings/telegram"),
      ]);
      setBookings(b.data);
      setPosts(p.data);
      setMessages(m.data);
      setSmtp(s.data);
      setTelegram(t.data);
    } catch (e) {
      toast.error("Nie udało się pobrać danych");
    }
  };

  useEffect(() => { loadAll(); }, []);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  // BOOKINGS
  const updateBookingStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success("Status zaktualizowany");
      loadAll();
      if (selectedBooking?.id === id) setSelectedBooking({ ...selectedBooking, status });
    } catch (e) {
      toast.error("Błąd aktualizacji");
    }
  };
  const deleteBooking = async (id) => {
    if (!window.confirm("Usunąć rezerwację?")) return;
    await api.delete(`/admin/bookings/${id}`);
    toast.success("Usunięto");
    setSelectedBooking(null);
    loadAll();
  };

  // BLOG
  const openNewPost = () => setEditingPost({ title: "", slug: "", excerpt: "", content: "", category: "", cover_image: "", published: true });
  const savePost = async () => {
    setSavingPost(true);
    try {
      if (editingPost.id) {
        await api.put(`/admin/blog/${editingPost.id}`, editingPost);
      } else {
        await api.post("/admin/blog", editingPost);
      }
      toast.success("Zapisano artykuł");
      setEditingPost(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Błąd zapisu");
    } finally {
      setSavingPost(false);
    }
  };
  const deletePost = async (id) => {
    if (!window.confirm("Usunąć artykuł?")) return;
    await api.delete(`/admin/blog/${id}`);
    toast.success("Usunięto");
    loadAll();
  };

  // MESSAGES
  const deleteMessage = async (id) => {
    if (!window.confirm("Usunąć wiadomość?")) return;
    await api.delete(`/admin/contact/${id}`);
    loadAll();
  };

  const stats = {
    total: bookings.length,
    new: bookings.filter((b) => b.status === "nowa").length,
    confirmed: bookings.filter((b) => b.status === "potwierdzona").length,
    posts: posts.length,
    messages: messages.length,
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-[#E5E0D8] sticky top-0 z-30">
        <div className="container-prose flex items-center justify-between h-16">
          <Link to="/" className="font-display text-xl text-[#2B2B2B]">
            Agnieszka <span className="text-[#8A9A86]">·</span> Panel
          </Link>
          <div className="flex items-center gap-6">
            <span className="text-xs text-[#5C6670] hidden md:inline">{admin?.email}</span>
            <button onClick={doLogout} data-testid="admin-logout" className="text-sm text-[#5C6670] hover:text-[#2B2B2B] inline-flex items-center gap-2">
              <LogOut size={14} /> Wyloguj
            </button>
          </div>
        </div>
      </header>

      <main className="container-prose py-10">
        {/* Stan powiadomien - glosno, gdy zgloszenia nikogo nie powiadamiaja */}
        {smtp && (!smtp.enabled || smtp.last_error || smtp.failed_notifications > 0) && (
          <div
            className="mb-8 border border-[#C88E75] bg-[#FBF3EF] rounded-2xl p-5 flex flex-wrap items-start justify-between gap-4"
            data-testid="smtp-warning-banner"
          >
            <div>
              <p className="text-overline text-[#C88E75] mb-2">Powiadomienia e-mail</p>
              {!smtp.enabled && (
                <p className="text-sm text-[#2B2B2B]">
                  Powiadomienia są wyłączone. Zgłoszenia z formularzy zapisują się w panelu, ale nikt nie dostaje
                  o nich wiadomości.
                </p>
              )}
              {smtp.enabled && smtp.last_error && (
                <p className="text-sm text-[#2B2B2B]">
                  Ostatnia wysyłka nie powiodła się: <span className="font-mono">{smtp.last_error}</span>
                </p>
              )}
              {smtp.failed_notifications > 0 && (
                <p className="text-sm text-[#2B2B2B] mt-1">
                  Zgłoszeń bez wysłanego powiadomienia: {smtp.failed_notifications}. Są zapisane i widoczne
                  w zakładkach Rezerwacje oraz Wiadomości.
                </p>
              )}
            </div>
            <button
              onClick={() => setTab(TAB.SETTINGS)}
              data-testid="smtp-banner-fix"
              className="px-5 py-2 rounded-xl bg-[#2B2B2B] text-white text-sm whitespace-nowrap"
            >
              Skonfiguruj
            </button>
          </div>
        )}

        {telegram && (!telegram.enabled || telegram.last_error || telegram.failed_notifications > 0) && (
          <div
            className="mb-8 border border-[#C88E75] bg-[#FBF3EF] rounded-2xl p-5 flex flex-wrap items-start justify-between gap-4"
            data-testid="telegram-warning-banner"
          >
            <div>
              <p className="text-overline text-[#C88E75] mb-2">Powiadomienia Telegram</p>
              {!telegram.enabled && (
                <p className="text-sm text-[#2B2B2B]">
                  Powiadomienia na Telegram są wyłączone. Zgłoszenia z formularzy zapisują się w panelu, ale nikt
                  nie dostaje o nich wiadomości na Telegramie.
                </p>
              )}
              {telegram.enabled && telegram.last_error && (
                <p className="text-sm text-[#2B2B2B]">
                  Ostatnia wysyłka nie powiodła się: <span className="font-mono">{telegram.last_error}</span>
                </p>
              )}
              {telegram.failed_notifications > 0 && (
                <p className="text-sm text-[#2B2B2B] mt-1">
                  Zgłoszeń bez wysłanego powiadomienia na Telegram: {telegram.failed_notifications}. Są zapisane
                  i widoczne w zakładkach Rezerwacje oraz Wiadomości.
                </p>
              )}
            </div>
            <button
              onClick={() => setTab(TAB.SETTINGS)}
              data-testid="telegram-banner-fix"
              className="px-5 py-2 rounded-xl bg-[#2B2B2B] text-white text-sm whitespace-nowrap"
            >
              Skonfiguruj
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          <StatCard label="Wszystkie rezerwacje" value={stats.total} />
          <StatCard label="Nowe" value={stats.new} accent />
          <StatCard label="Potwierdzone" value={stats.confirmed} />
          <StatCard label="Artykuły" value={stats.posts} />
          <StatCard label="Wiadomości" value={stats.messages} />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[#E5E0D8] mb-8">
          <TabButton active={tab === TAB.BOOKINGS} onClick={() => setTab(TAB.BOOKINGS)} icon={Calendar} label="Rezerwacje" testId="tab-bookings" />
          <TabButton active={tab === TAB.BLOG} onClick={() => setTab(TAB.BLOG)} icon={BookOpen} label="Blog" testId="tab-blog" />
          <TabButton active={tab === TAB.MESSAGES} onClick={() => setTab(TAB.MESSAGES)} icon={Mail} label="Wiadomości" testId="tab-messages" />
          <TabButton active={tab === TAB.CONTENT} onClick={() => setTab(TAB.CONTENT)} icon={FileText} label="Treść strony" testId="tab-content" />
          <TabButton active={tab === TAB.SETTINGS} onClick={() => setTab(TAB.SETTINGS)} icon={Settings} label="Ustawienia" testId="tab-settings" />
          <TabButton active={tab === TAB.MANUAL} onClick={() => setTab(TAB.MANUAL)} icon={HelpCircle} label="Instrukcja" testId="tab-manual" />
        </div>

        {/* BOOKINGS */}
        {tab === TAB.BOOKINGS && (
          <div data-testid="bookings-tab">
            <div className="bg-white border border-[#E5E0D8] rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F2EEE7] text-[#5C6670] text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-6 py-4">Klient</th>
                    <th className="text-left px-6 py-4 hidden md:table-cell">Usługa</th>
                    <th className="text-left px-6 py-4 hidden lg:table-cell">Data</th>
                    <th className="text-left px-6 py-4">Status</th>
                    <th className="text-right px-6 py-4">Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-t border-[#E5E0D8] hover:bg-[#F9F8F6]" data-testid={`booking-row-${b.id}`}>
                      <td className="px-6 py-4">
                        <div className="font-medium text-[#2B2B2B]">{b.full_name}</div>
                        <div className="text-xs text-[#5C6670]">{b.email}</div>
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell text-[#2B2B2B]">{b.service}</td>
                      <td className="px-6 py-4 hidden lg:table-cell text-[#5C6670]">{b.preferred_date} · {b.preferred_time}</td>
                      <td className="px-6 py-4">
                        <select
                          value={b.status} onChange={(e) => updateBookingStatus(b.id, e.target.value)}
                          data-testid={`booking-status-${b.id}`}
                          className={`text-xs px-3 py-1.5 rounded-full border ${statusColor(b.status)}`}
                        >
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button onClick={() => setSelectedBooking(b)} data-testid={`view-booking-${b.id}`} className="text-[#8A9A86] hover:text-[#2B2B2B] mr-3 text-xs">Szczegóły</button>
                        <button onClick={() => deleteBooking(b.id)} data-testid={`delete-booking-${b.id}`} className="text-[#C88E75] hover:text-[#2B2B2B]"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                  {bookings.length === 0 && (
                    <tr><td colSpan={5} className="px-6 py-10 text-center text-[#5C6670]">Brak rezerwacji</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* BLOG */}
        {tab === TAB.BLOG && (
          <div data-testid="blog-tab">
            <div className="flex justify-end mb-6">
              <button onClick={openNewPost} data-testid="new-post-button" className="bg-[#2B2B2B] text-white rounded-full px-6 py-3 text-sm hover:bg-[#8A9A86] transition-colors inline-flex items-center gap-2">
                <Plus size={16} /> Nowy artykuł
              </button>
            </div>
            <div className="bg-white border border-[#E5E0D8] rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F2EEE7] text-[#5C6670] text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-6 py-4">Tytuł</th>
                    <th className="text-left px-6 py-4 hidden md:table-cell">Kategoria</th>
                    <th className="text-left px-6 py-4 hidden lg:table-cell">Slug</th>
                    <th className="text-left px-6 py-4">Status</th>
                    <th className="text-right px-6 py-4">Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((p) => (
                    <tr key={p.id} className="border-t border-[#E5E0D8] hover:bg-[#F9F8F6]" data-testid={`post-row-${p.id}`}>
                      <td className="px-6 py-4 text-[#2B2B2B] font-medium">{p.title}</td>
                      <td className="px-6 py-4 hidden md:table-cell text-[#5C6670]">{p.category}</td>
                      <td className="px-6 py-4 hidden lg:table-cell text-[#5C6670]">{p.slug}</td>
                      <td className="px-6 py-4">
                        <span className={`text-xs px-3 py-1.5 rounded-full ${p.published ? "bg-[#8A9A86]/15 text-[#5A6A56]" : "bg-[#E5E0D8] text-[#5C6670]"}`}>
                          {p.published ? "Opublikowany" : "Szkic"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button onClick={() => setEditingPost(p)} data-testid={`edit-post-${p.id}`} className="text-[#8A9A86] hover:text-[#2B2B2B] mr-3"><Edit3 size={14} /></button>
                        <button onClick={() => deletePost(p.id)} data-testid={`delete-post-${p.id}`} className="text-[#C88E75] hover:text-[#2B2B2B]"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                  {posts.length === 0 && (
                    <tr><td colSpan={5} className="px-6 py-10 text-center text-[#5C6670]">Brak artykułów</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* MESSAGES */}
        {tab === TAB.MESSAGES && (
          <div className="space-y-4" data-testid="messages-tab">
            {messages.map((m) => (
              <div key={m.id} className="bg-white border border-[#E5E0D8] rounded-2xl p-6" data-testid={`message-${m.id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-medium text-[#2B2B2B]">{m.name}</p>
                    <p className="text-xs text-[#5C6670]">{m.email} · {new Date(m.created_at).toLocaleString("pl-PL")}</p>
                  </div>
                  <button onClick={() => deleteMessage(m.id)} className="text-[#C88E75]" data-testid={`delete-message-${m.id}`}><Trash2 size={14} /></button>
                </div>
                <p className="text-[#2B2B2B] whitespace-pre-wrap text-sm leading-relaxed">{m.message}</p>
              </div>
            ))}
            {messages.length === 0 && <p className="text-[#5C6670] text-center py-12">Brak wiadomości</p>}
          </div>
        )}

        {/* CONTENT */}
        {tab === TAB.CONTENT && <ContentEditor />}

        {/* SETTINGS */}
        {tab === TAB.SETTINGS && (
          <>
            <SmtpSettings onHealthChange={setSmtp} />
            <TelegramSettings onHealthChange={setTelegram} />
            <ChangePassword />
          </>
        )}

        {/* MANUAL */}
        {tab === TAB.MANUAL && <Manual />}
      </main>

      {/* Booking detail dialog */}
      <Dialog open={!!selectedBooking} onOpenChange={(o) => !o && setSelectedBooking(null)}>
        <DialogContent className="max-w-2xl bg-white" data-testid="booking-detail-dialog">
          {selectedBooking && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-[#2B2B2B]">{selectedBooking.full_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm py-4 max-h-[60vh] overflow-y-auto">
                <Row k="Email" v={selectedBooking.email} />
                <Row k="Telefon" v={selectedBooking.phone} />
                <Row k="Filar" v={selectedBooking.pillar} />
                <Row k="Usługa" v={selectedBooking.service} />
                <Row k="Termin" v={`${selectedBooking.preferred_date} · ${selectedBooking.preferred_time}`} />
                <Row k="Forma" v={selectedBooking.session_format} />
                <Row k="Status" v={selectedBooking.status} />
                <div className="pt-3 border-t border-[#E5E0D8]">
                  <p className="text-overline mb-2">Intencja</p>
                  <p className="text-[#2B2B2B] whitespace-pre-wrap">{selectedBooking.intention}</p>
                </div>
                {selectedBooking.current_state && (
                  <div>
                    <p className="text-overline mb-2">Obecny stan</p>
                    <p className="text-[#2B2B2B] whitespace-pre-wrap">{selectedBooking.current_state}</p>
                  </div>
                )}
                {selectedBooking.previous_experience && (
                  <div>
                    <p className="text-overline mb-2">Wcześniejsze doświadczenie</p>
                    <p className="text-[#2B2B2B] whitespace-pre-wrap">{selectedBooking.previous_experience}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Post editor dialog */}
      <Dialog open={!!editingPost} onOpenChange={(o) => !o && setEditingPost(null)}>
        <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="post-editor-dialog">
          {editingPost && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{editingPost.id ? "Edycja artykułu" : "Nowy artykuł"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-5 py-4">
                <InputField label="Tytuł" value={editingPost.title} onChange={(v) => setEditingPost({ ...editingPost, title: v })} testId="post-title" />
                <InputField label="Slug" value={editingPost.slug} onChange={(v) => setEditingPost({ ...editingPost, slug: v })} testId="post-slug" placeholder="np. moj-pierwszy-artykul" />
                <InputField label="Kategoria" value={editingPost.category} onChange={(v) => setEditingPost({ ...editingPost, category: v })} testId="post-category" />
                <InputField label="Cover Image URL" value={editingPost.cover_image} onChange={(v) => setEditingPost({ ...editingPost, cover_image: v })} testId="post-cover" />
                <TextAreaField label="Zajawka" value={editingPost.excerpt} onChange={(v) => setEditingPost({ ...editingPost, excerpt: v })} rows={3} testId="post-excerpt" />
                <TextAreaField label="Treść" value={editingPost.content} onChange={(v) => setEditingPost({ ...editingPost, content: v })} rows={10} testId="post-content" />
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editingPost.published} onChange={(e) => setEditingPost({ ...editingPost, published: e.target.checked })} data-testid="post-published" />
                  Opublikowany
                </label>
              </div>
              <DialogFooter>
                <button onClick={() => setEditingPost(null)} className="text-[#5C6670] text-sm px-4 py-2">Anuluj</button>
                <button onClick={savePost} disabled={savingPost} data-testid="post-save" className="bg-[#2B2B2B] text-white rounded-full px-6 py-2 text-sm hover:bg-[#8A9A86] disabled:opacity-50">
                  {savingPost ? "Zapisywanie…" : "Zapisz"}
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className={`rounded-2xl p-5 border ${accent ? "bg-[#8A9A86] text-white border-[#8A9A86]" : "bg-white border-[#E5E0D8]"}`}>
      <p className={`text-xs uppercase tracking-widest mb-2 ${accent ? "text-white/80" : "text-[#5C6670]"}`}>{label}</p>
      <p className="font-display text-3xl">{value}</p>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`px-5 py-3 inline-flex items-center gap-2 text-sm border-b-2 -mb-px transition-colors ${
        active ? "border-[#8A9A86] text-[#2B2B2B]" : "border-transparent text-[#5C6670] hover:text-[#2B2B2B]"
      }`}>
      <Icon size={14} /> {label}
    </button>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex gap-4">
      <span className="text-overline w-32 shrink-0">{k}</span>
      <span className="text-[#2B2B2B]">{v}</span>
    </div>
  );
}

function InputField({ label, value, onChange, testId, placeholder }) {
  return (
    <div>
      <label className="text-overline block mb-2">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} placeholder={placeholder}
        className="w-full bg-transparent border-b border-[#E5E0D8] py-2.5 focus:outline-none focus:border-[#8A9A86]" />
    </div>
  );
}

function TextAreaField({ label, value, onChange, rows = 4, testId }) {
  return (
    <div>
      <label className="text-overline block mb-2">{label}</label>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows} data-testid={testId}
        className="w-full bg-transparent border border-[#E5E0D8] rounded-xl p-3 focus:outline-none focus:border-[#8A9A86] resize-none" />
    </div>
  );
}

function statusColor(s) {
  switch (s) {
    case "nowa": return "bg-[#8A9A86]/15 text-[#5A6A56] border-[#8A9A86]/30";
    case "potwierdzona": return "bg-[#C88E75]/15 text-[#A06C57] border-[#C88E75]/30";
    case "odbyta": return "bg-[#E5E0D8] text-[#5C6670] border-[#E5E0D8]";
    case "anulowana": return "bg-red-50 text-red-700 border-red-100";
    default: return "border-[#E5E0D8]";
  }
}
